import { Card, Flex, Link, Text } from '@servicetitan/anvil2';
import { planCard } from './PlanCard.module.scss';
import { Container as MonthlyPaymentContainer } from '../monthly-payment/Container';
import { SelectPlanButton } from './SelectPlanButton';
import { SelectedPlanStore } from './selectedPlanStore';
import {
    Calculation,
    PaymentStructure,
    PaymentFrequency,
} from '@servicetitan/financing-api-client';
import { useState } from 'react';
import { TermsDrawer } from './TermsDrawer';
import { PlanTags } from './PlanTags';
import { useProviderConfig } from '@servicetitan/financing-workflow-shared';
import { HtmlContent } from '@servicetitan/financing-utils-shared';

interface PlanCardProps {
    plan: Calculation;
    onPlanSelected?: (plan: Calculation) => void;
    showLowestPayment: boolean;
    currentFrequency?: PaymentFrequency;
    disableSelection?: boolean;
    /**
     * Renders the card in its out-of-range form (used by the pre-approval card when the financed
     * amount falls outside the plan's eligible range): the payment row shows `$--` in place of the
     * real figures, View Terms remains visible (terms are plan-level and always populated), the
     * "lowest" chips are suppressed, and the Select Plan button is disabled. Plan-level content
     * (structure chip, Max APR, description) is kept.
     */
    unavailable?: boolean;
}

export const PlanCard = ({
    plan,
    onPlanSelected,
    showLowestPayment,
    currentFrequency,
    disableSelection = false,
    unavailable = false,
}: PlanCardProps) => {
    const { setSelectedPlan } = SelectedPlanStore.useStore();
    const [isTermsOpen, setIsTermsOpen] = useState(false);
    const { showPlanTags, providerCategory, hideDeferredInterestMaxApr, showAmortization } =
        useProviderConfig();
    // `Calculation` (fin-plans-api) and the imported `PaymentStructure` (financing-api) are two
    // identically-valued generated enums; reconcile them so the comparisons are enum-type-safe.
    const paymentStructure = plan.paymentStructure as PaymentStructure;
    const hideMaxAPR =
        paymentStructure === PaymentStructure.DeferredInterest && hideDeferredInterestMaxApr;

    const amortizationMonths = plan.amortization;

    return (
        <Card
            style={{ maxWidth: '379px', minWidth: '287px', maxHeight: '500px' }}
            className={planCard}
            role="listitem"
        >
            <Flex direction="column" gap="3" style={{ width: '100%' }}>
                {showPlanTags && (
                    <PlanTags plan={plan} showLowestPayment={showLowestPayment && !unavailable} />
                )}
                <MonthlyPaymentContainer
                    plan={plan}
                    currentFrequency={currentFrequency}
                    placeholder={unavailable}
                />
                {!hideMaxAPR && (
                    <div>
                        <Text variant="eyebrow">{showAmortization ? 'APR' : 'Max APR'}</Text>
                        <Text aria-label={showAmortization ? 'APR' : 'Max APR'}>
                            {plan.defaultRate}%
                        </Text>
                    </div>
                )}
                {/* Amortization - use amortization field from plan data */}
                {showAmortization && amortizationMonths && (
                    <div>
                        <Text variant="eyebrow">Amortization Period</Text>
                        <Text aria-label="Amortization">{amortizationMonths} months</Text>
                    </div>
                )}
                {providerCategory === 'second-look' &&
                paymentStructure === PaymentStructure.DeferredInterestAndPrincipal ? (
                    <div>
                        <Text variant="eyebrow">DEFERRED INTEREST</Text>
                        <Text aria-label="Promotional Terms">{plan.promotionalTerms} months</Text>
                    </div>
                ) : providerCategory !== 'second-look' ? (
                    <div style={{ marginTop: 'auto' }}>
                        <Text variant="eyebrow">Description</Text>
                        <HtmlContent content={plan.displayDescription ?? plan.description ?? ''} />
                    </div>
                ) : null}
                {providerCategory !== 'second-look' && (
                    <Link ghost onClick={() => setIsTermsOpen(true)}>
                        View Terms
                    </Link>
                )}
                <span style={{ width: '100%', marginTop: 'auto' }}>
                    <hr
                        style={{
                            width: '100%',
                            borderBlock: 'unset',
                            borderBlockStart: '0.0625rem solid var(--a2-border-color-subdued)',
                        }}
                    />
                </span>
                <SelectPlanButton
                    onClick={isSelected => {
                        setSelectedPlan(isSelected ? plan : null);
                        if (isSelected) {
                            onPlanSelected?.(plan);
                        }
                    }}
                    planNumber={plan.planNumber!}
                    providerName={plan.providerName}
                    disabled={disableSelection || unavailable}
                />
                {isTermsOpen && (
                    <TermsDrawer
                        open={isTermsOpen}
                        onClose={() => setIsTermsOpen(false)}
                        terms={plan.financingTerms}
                    />
                )}
            </Flex>
        </Card>
    );
};
